import { useEffect, useRef, useState } from 'react';
import { Upload, Loader2, ImageIcon } from 'lucide-react';
import { uploadImage } from '@/lib/api';
import { normalizePublicImageUrl } from '@/lib/format';

interface ImageUploadProps {
  value: string;
  onChange: (url: string) => void;
  label?: string;
  aspect?: 'square' | 'wide';
  fileNameHint?: string;
  uploadOnSelect?: boolean;
  onFileSelect?: (file: File | null) => void;
}

export function ImageUpload({
  value,
  onChange,
  label = 'Imagem',
  aspect = 'wide',
  fileNameHint = '',
  uploadOnSelect = true,
  onFileSelect,
}: ImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [localPreviewUrl, setLocalPreviewUrl] = useState('');

  useEffect(() => {
    if (!value || !localPreviewUrl) return;
    URL.revokeObjectURL(localPreviewUrl);
    setLocalPreviewUrl('');
  }, [value, localPreviewUrl]);

  useEffect(() => {
    return () => {
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
      }
    };
  }, [localPreviewUrl]);

  const handleFile = async (file: File) => {
    const previewUrl = URL.createObjectURL(file);
    setLocalPreviewUrl(previewUrl);

    if (!uploadOnSelect) {
      onFileSelect?.(file);
      return;
    }

    setUploading(true);
    try {
      const response = await uploadImage(file, fileNameHint);
      if (!response.success || !response.url) throw new Error(response.message || 'Erro ao enviar imagem');
      onFileSelect?.(null);
      onChange(response.url);
    } catch (err) {
      URL.revokeObjectURL(previewUrl);
      setLocalPreviewUrl('');
      alert('Erro ao enviar imagem. Tente novamente.');
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const aspectClass = aspect === 'square' ? 'aspect-square' : 'aspect-video';
  const previewSrc = localPreviewUrl || normalizePublicImageUrl(value);

  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-warm-700">{label}</label>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
        }}
      />
      <div
        onClick={() => inputRef.current?.click()}
        className={`relative cursor-pointer overflow-hidden rounded-2xl border-2 border-dashed border-warm-300 bg-warm-50 ${aspectClass} flex items-center justify-center transition-colors hover:border-brand-400 hover:bg-brand-50`}
      >
        {previewSrc ? (
          <>
            <img src={previewSrc} alt="Preview" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-black/0 transition-colors hover:bg-black/20" />
          </>
        ) : uploading ? (
          <Loader2 className="h-8 w-8 animate-spin text-warm-400" />
        ) : (
          <div className="flex flex-col items-center gap-2 text-warm-400">
            {aspect === 'square' ? <ImageIcon size={32} /> : <Upload size={28} />}
            <span className="text-sm font-medium">Clique para enviar</span>
          </div>
        )}
      </div>
    </div>
  );
}
